<template>
    <div style="z-index: 100;" :class="{ 'fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center': isModal }" v-if="isVisible">
        <div
            :class="isModal ? 'relative bg-white rounded-lg w-full max-w-xl p-6 mx-4' : 'w-full max-w-md p-6 mx-auto mt-8 bg-white shadow-md rounded-lg'">

            <!-- Close Button untuk Modal -->
            <button v-if="isModal" @click="closeModal" class="absolute top-8 right-6 text-gray-500 focus:outline-none"
                :disabled="isLoadingSubmit || isLoadingOTP">
                <AppIcon icon="mdi:close" class="w-6 h-6" />
            </button>

            <!-- Title -->
            <h2 class="text-xl font-semibold mb-4">{{ $t('phoneNumberChange.title') }}</h2>
            <hr class="mb-4">

            <!-- Phone Number Field -->
            <MazPhoneNumberInput v-model="phoneNumber" v-model:country-code="countryCode" show-code-on-list
                :defaultCountryCode="'ID'" :preferred-countries="['ID', 'MY', 'SG', 'US', 'TH']"
                :ignored-countries="['AC']" @update="handlePhoneNumberUpdate" class="custom-phone-input w-full mb-4"
                :disabled="isOTPSent || isLoadingOTP || isLoadingSubmit" />

            <!-- Password Field -->
            <div class="mb-4">
                <BaseInput type="password" placeholder="Password" v-model="form.password"
                    :class="{ 'border-red-500': errors.password }" :disabled="isLoadingSubmit || isLoadingOTP" />
            </div>

            <!-- OTP Input Field dan Button dalam Satu Baris -->
            <div class="flex items-center gap-2 mb-4">
                <!-- Verification Code Input -->
                <div class="flex-1">
                    <BaseInput type="text" placeholder="Verification Code" v-model="form.verificationCode"
                        :class="{ 'border-red-500': errors.verificationCode }"
                        :disabled="isLoadingSubmit || isLoadingOTP" />
                </div>

                <!-- Send OTP Button -->
                <div>
                    <button @click.prevent="triggerSendOTP"
                        class="py-2 px-4 mb-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2" :class="{
                            'bg-green-500 text-white hover:bg-green-600 focus:ring-green-600': !isLoadingOTP && !isOTPTimerActive,
                            'bg-gray-300 text-gray-500 cursor-not-allowed': isLoadingOTP || isOTPTimerActive
                        }" :disabled="isLoadingOTP || !parsedPhoneNumber || isOTPTimerActive">
                        <span v-if="isLoadingOTP">{{ $t('phoneNumberChange.otpSending') }}</span>
                        <span v-else-if="isOTPTimerActive">{{ otpTimer }}s</span>
                        <span v-else>{{ $t('phoneNumberChange.sendOTP') }}</span>
                    </button>
                </div>
            </div>

            <!-- Submit Button -->
            <div class="flex flex-row justify-end">
                <button type="submit" @click.prevent="submitForm"
                    class="py-2 px-4 mt-4 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                    :class="{
                        'bg-blue-500 text-white': !isLoadingSubmit && isFormValid,
                        'bg-gray-300 text-gray-500 cursor-not-allowed': isLoadingSubmit || !isFormValid
                    }" :disabled="isLoadingSubmit || !isFormValid">
                    <span v-if="isLoadingSubmit">{{ $t('phoneNumberChange.submitLoading') }}</span>
                    <span v-else>{{ $t('phoneNumberChange.submitButton') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
// maz-ui + libphonenumber-js (~405 KB) ikut chunk AWAL selama impornya statis: ketiga
// komponen ini terdaftar global lewat require.context di components/modules/index.js.
// Dimuat saat dipakai saja — semuanya field di form booking/profil, bukan halaman depan.
// ponytail: kelambanan ditaruh di tempat impor beratnya, bukan sebagai daftar-nama di
// index.js — komponen maz-ui berikutnya tinggal ikut pola ini, tak ada daftar yang basi.
import { mapActions } from 'vuex';
import { getResponseMessage } from "@/utils/responseMapping";

export default {
    name: "ChangePhoneNumber",
    components: {
        MazPhoneNumberInput: defineAsyncComponent(() =>
            import("maz-ui/components/MazPhoneNumberInput")
        ),
    },
    props: {
        isModal: {
            type: Boolean,
            default: false
        },
        isVisible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            phoneNumber: '',
            countryCode: 'ID',
            parsedPhoneNumber: '',
            form: {
                password: '',
                verificationCode: ''
            },
            errors: {
                phoneNumber: '',
                password: '',
                verificationCode: ''
            },
            isLoadingOTP: false, // Loading state untuk OTP
            isLoadingSubmit: false, // Loading state untuk Submit
            isOTPSent: false,
            isOTPTimerActive: false,
            otpTimer: 180 // Timer 3 menit dalam detik
        };
    },
    computed: {
        isFormValid() {
            return !!this.parsedPhoneNumber && !!this.form.password && !!this.form.verificationCode;
        }
    },
    methods: {
        ...mapActions('user', ['sendOTP', 'changePhoneNumber']),

        validateForm() {
            this.errors.phoneNumber = !this.parsedPhoneNumber
                ? this.$t('phoneNumberChange.phoneNumberRequired')
                : '';
            this.errors.password = !this.form.password
                ? this.$t('phoneNumberChange.passwordRequired')
                : '';
            this.errors.verificationCode = !this.form.verificationCode
                ? this.$t('phoneNumberChange.verificationCodeRequired')
                : '';
        },

        handlePhoneNumberUpdate(results) {
            if (results && results.e164) {
                this.parsedPhoneNumber = results.e164;
            }
        },

        closeModal() {
            this.form.password = '';
            this.form.verificationCode = '';
            this.phoneNumber = '';
            this.parsedPhoneNumber = '';
            this.errors.password = '';
            this.errors.verificationCode = '';
            this.errors.phoneNumber = '';
            this.isLoadingSubmit = false;
            this.isLoadingOTP = false;
            this.isOTPSent = false;
            this.isOTPTimerActive = false;
            this.otpTimer = 180;
            this.$emit('close');
        },

        async triggerSendOTP() {
            if (!this.parsedPhoneNumber) {
                this.errors.phoneNumber = 'Valid phone number is required.';
                this.$alert.toast('warning', 'Please enter a valid phone number.');
                return;
            }

            try {
                this.isLoadingOTP = true;
                this.isOTPSent = true;

                const payload = {
                    PhoneNumber: this.parsedPhoneNumber,
                    Email: '',
                    SendBy: 1,
                    Type: 4 //change phone number 3: change email
                };

                const response = await this.sendOTP(payload);

                this.$alert.toast('success', 'OTP has been sent successfully.');

                const timerDuration = response?.data.data || 300; // Default ke 180 detik jika 'data' tidak ada

                this.startOTPTimer(timerDuration);
            } catch (error) {
                const errorMessage = error.response?.data?.msg || 'Failed to send OTP.';
                this.$alert.toast('error', errorMessage);
            } finally {
                this.isLoadingOTP = false;
            }
        },

        startOTPTimer(duration) {
            this.isOTPTimerActive = true;
            this.otpTimer = duration || 300; // Gunakan durasi dari parameter, fallback ke 180 detik

            const interval = setInterval(() => {
                if (this.otpTimer > 0) {
                    this.otpTimer--;
                } else {
                    clearInterval(interval);
                    this.isOTPTimerActive = false;
                    this.isOTPSent = false;
                }
            }, 1000); // Interval dijalankan setiap 1 detik
        },

        async submitForm() {
            this.validateForm();

            if (!this.errors.phoneNumber && !this.errors.password && !this.errors.verificationCode) {
                this.isLoadingSubmit = true; // Aktifkan state loading

                try {
                    const payload = {
                        NewPhoneNumber: this.parsedPhoneNumber,
                        Password: this.form.password,
                        VerificationCode: this.form.verificationCode
                    };

                    const response = await this.changePhoneNumber(payload);

                    console.log("response:", response);

                    // Periksa apakah code API adalah 0 (sukses)
                    if (response?.data?.code === 0) {
                        const successMessage = getResponseMessage(response.status, response.data.code);
                        this.$alert.toast('success', successMessage || this.$t('phoneNumberChange.success'));
                        this.closeModal();
                    } else {
                        // Jika code bukan 0, tangani sebagai error
                        const errorMessage = getResponseMessage(response.status, response.data.code);
                        this.$alert.toast('warning', errorMessage || this.$t('phoneNumberChange.error'));
                    }
                } catch (error) {
                    console.error('API Error:', error);
                    const errorCode = error.response?.data?.code;
                    const errorStatus = error.response?.status;

                    // Jika password salah
                    if (errorCode === 1 && errorStatus === 400) {
                        this.errors.password = this.$t('phoneNumberChange.invalidPassword');
                        this.form.password = ''; // Reset password field
                        this.$alert.toast('warning', this.$t('phoneNumberChange.invalidPassword'));
                    } else {
                        const validationErrors = error.response?.data?.data || [];
                        validationErrors.forEach((err) => {
                            if (err.property === 'NewPhoneNumber') {
                                this.errors.phoneNumber = err.error?.minLength || this.$t('phoneNumberChange.phoneNumberMinLength');
                            }
                            if (err.property === 'VerificationCode') {
                                this.errors.verificationCode = err.error?.minLength || this.$t('phoneNumberChange.verificationCodeMinLength');
                            }
                        });

                        const errorMessage = getResponseMessage(error.response?.status, error.response?.data?.code);
                        this.$alert.toast('warning', errorMessage || this.$t('phoneNumberChange.validationFailed'));
                    }
                } finally {
                    this.isLoadingSubmit = false; // Matikan state loading
                }
            } else {
                this.$alert.toast('warning', this.$t('phoneNumberChange.validationFailed'));
            }
        }
    }
};
</script>

<style scoped>
.custom-phone-input {
    width: 100% !important;
    max-width: 100% !important;
}

::v-deep(.m-input-wrapper) {
    width: 100% !important;
}

::v-deep(input) {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
}
</style>